import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Link2, Unplug, RefreshCw, Bot, FolderOpen, Save, CheckCircle2, AlertTriangle } from "lucide-react";
import { tarifasApi, errMsg, fmtDate, pollRun } from "./api";

function Dot({ ok }) {
  return ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />;
}

export const ConnectionsBar = ({ onCatalogSynced }) => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState("");
  const [syncRun, setSyncRun] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([tarifasApi.googleStatus(), tarifasApi.config()]);
      setStatus(s);
      setConfig(c);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo leer el estado de las conexiones"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy("connect");
    try {
      const { authorization_url } = await tarifasApi.googleConnect();
      // Google no permite autorizar dentro de un iframe (vista previa incrustada): abrimos pestaña propia.
      const win = window.open(authorization_url, "_blank", "noopener");
      if (!win) {
        window.location.href = authorization_url;
        return;
      }
      toast.info("Completa la autorización en la pestaña de Google que se ha abierto. Esta pantalla se actualizará sola.");
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await tarifasApi.googleStatus();
        if (s.connected) {
          toast.success("Google Drive conectado correctamente");
          await load();
          onCatalogSynced?.();
          break;
        }
      }
    } catch (e) {
      toast.error(errMsg(e, "No se pudo iniciar la conexión con Google"));
    } finally {
      setBusy("");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("¿Desconectar Google Drive? Podrás volver a conectarlo cuando quieras.")) return;
    await tarifasApi.googleDisconnect();
    toast.success("Google desconectado");
    load();
  };

  const syncCatalog = async () => {
    setBusy("sync");
    try {
      const run = await tarifasApi.syncCatalog();
      setSyncRun(run);
      const done = await pollRun(run.id, setSyncRun);
      if (done.status === "done") {
        const s = done.summary;
        toast.success(`Catálogo KimKim sincronizado: ${s.total} actividades (${s.espiritu_owned ?? s.total} de Espíritu Travel · ${s.with_prices} con precios)${s.errors ? ` · ${s.errors} sin detalle, se reintentarán` : ""}`, { duration: 10000 });
        if (done.message) toast.warning(done.message, { duration: 12000 });
        onCatalogSynced?.();
      } else toast.error(done.message || "La sincronización falló");
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo sincronizar el catálogo"));
    } finally {
      setBusy("");
      setSyncRun(null);
    }
  };

  const saveConfig = async () => {
    setBusy("config");
    try {
      setConfig(await tarifasApi.saveConfig(edit));
      setEdit(null);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(errMsg(e, "No se pudo guardar"));
    } finally {
      setBusy("");
    }
  };

  if (!status || !config) return <div className="py-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando conexiones…</div>;

  return (
    <Card className="rounded-md" data-testid="tarifas-connections">
      <CardContent className="p-4 grid gap-4 md:grid-cols-3">
        <div className="space-y-2" data-testid="google-connection">
          <div className="flex items-center gap-2 text-sm font-semibold"><Dot ok={status.connected} /> Google Drive</div>
          {!status.configured && (
            <p className="text-xs text-amber-700" data-testid="google-not-configured">Faltan las credenciales de Google (ID de cliente y secreto) en el servidor.</p>
          )}
          {status.connected ? (
            <>
              <p className="text-xs text-muted-foreground">Conectado como <strong>{status.email || "cuenta de Google"}</strong> · {fmtDate(status.updated_at)}</p>
              <Button data-testid="google-disconnect-button" size="sm" variant="outline" className="rounded-md" onClick={disconnect}><Unplug className="h-3 w-3 mr-1" /> Desconectar</Button>
            </>
          ) : (
            <Button data-testid="google-connect-button" size="sm" className="rounded-md" onClick={connect} disabled={!status.configured || busy === "connect"}>
              {busy === "connect" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />} Conectar Google
            </Button>
          )}
        </div>

        <div className="space-y-2" data-testid="kimkim-connection">
          <div className="flex items-center gap-2 text-sm font-semibold"><Dot ok={!!config.last_catalog_sync_at} /> Catálogo KimKim</div>
          <p className="text-xs text-muted-foreground">Última sincronización: {fmtDate(config.last_catalog_sync_at)}</p>
          <Button data-testid="kimkim-sync-button" size="sm" variant="outline" className="rounded-md" onClick={syncCatalog} disabled={busy === "sync"}>
            {busy === "sync" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />} Sincronizar catálogo
          </Button>
          {syncRun && <p className="text-xs text-blue-700" data-testid="kimkim-sync-progress">{syncRun.message || syncRun.status}</p>}
        </div>

        <div className="space-y-2" data-testid="ai-connection">
          <div className="flex items-center gap-2 text-sm font-semibold"><Dot ok={status.llm_configured} /> <Bot className="h-4 w-4" /> IA de lectura de tarifas</div>
          <p className="text-xs text-muted-foreground">{status.llm_configured ? "GPT (OpenAI) con la clave universal de Emergent" : "Sin clave de IA configurada"}</p>
          <Badge className="rounded-md bg-slate-100 text-slate-700 hover:bg-slate-100">Recargo fijo: neto × 1,08, redondeado al euro superior</Badge>
        </div>

        <div className="md:col-span-3 border-t pt-3">
          {edit ? (
            <div className="grid gap-2 md:grid-cols-[1fr_1fr_120px_auto] items-end">
              <label className="text-xs">Carpeta de Drive (Tarifas)
                <Input data-testid="config-folder-input" value={edit.drive_folder_url} onChange={(e) => setEdit({ ...edit, drive_folder_url: e.target.value })} className="h-8 text-xs rounded-md mt-1" />
              </label>
              <label className="text-xs">URL del catálogo KimKim (listado de actividades de empresa)
                <Input data-testid="config-catalog-input" value={edit.catalog_url} onChange={(e) => setEdit({ ...edit, catalog_url: e.target.value })} className="h-8 text-xs rounded-md mt-1" />
              </label>
              <label className="text-xs">Año objetivo
                <Input data-testid="config-year-input" type="number" value={edit.target_year} onChange={(e) => setEdit({ ...edit, target_year: Number(e.target.value) })} className="h-8 text-xs rounded-md mt-1" />
              </label>
              <div className="flex gap-2">
                <Button data-testid="config-save-button" size="sm" className="rounded-md" onClick={saveConfig} disabled={busy === "config"}><Save className="h-3 w-3 mr-1" /> Guardar</Button>
                <Button size="sm" variant="ghost" className="rounded-md" onClick={() => setEdit(null)}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><FolderOpen className="h-3 w-3" /> Carpeta: <a className="underline" href={config.drive_folder_url} target="_blank" rel="noreferrer" data-testid="config-folder-link">{config.root_folder_name || "Tarifas"}</a></span>
              <span>Año objetivo: <strong>{config.target_year}</strong></span>
              <span>Último escaneo de Drive: {fmtDate(config.last_scan_at)}</span>
              <Button data-testid="config-edit-button" size="sm" variant="ghost" className="rounded-md h-7" onClick={() => setEdit({ drive_folder_url: config.drive_folder_url, catalog_url: config.catalog_url, target_year: config.target_year })}>Editar configuración</Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
